import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { loadPdfJs } from '@/utils/pdfLoader';
import { getFieldConfig } from '@/utils/pdfFieldConfig';

interface SignatureFieldOverlay {
  field_type: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DocumentViewerProps {
  pdfUrl: string;
  signatureFields?: SignatureFieldOverlay[];
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  pdfUrl,
  signatureFields,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<any>(null);

  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [userZoom, setUserZoom] = useState<number | null>(null);
  const [fitScale, setFitScale] = useState(1);

  const currentScale = userZoom ?? fitScale;

  const currentPageFields = useMemo(() => {
    if (!signatureFields) return [];
    return signatureFields.filter((f) => f.page === currentPage);
  }, [signatureFields, currentPage]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const pdfjsLib = await loadPdfJs();
        const doc = await pdfjsLib.getDocument(pdfUrl).promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        setCurrentPage(1);
      } catch (err) {
        console.error('Failed to load PDF:', err);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  const renderPage = useCallback(async () => {
    const doc = pdfDocRef.current;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!doc || !canvas || !container) return;

    setLoading(true);

    try {
      const page = await doc.getPage(currentPage);
      const baseViewport = page.getViewport({ scale: 1 });
      const containerWidth = container.clientWidth;
      const computedFitScale = containerWidth / baseViewport.width;

      if (userZoom === null) {
        setFitScale(computedFitScale);
      }

      const scale = userZoom ?? computedFitScale;
      const viewport = page.getViewport({ scale });
      const dpr = window.devicePixelRatio || 1;

      canvas.width = viewport.width * dpr;
      canvas.height = viewport.height * dpr;
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      await page.render({ canvasContext: ctx, viewport }).promise;
    } catch (err) {
      console.error('Failed to render page:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, userZoom]);

  useEffect(() => {
    if (numPages > 0) {
      renderPage();
    }
  }, [numPages, renderPage]);

  const handleZoomIn = () => {
    setUserZoom((prev) => Math.min((prev ?? fitScale) + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setUserZoom((prev) => Math.max((prev ?? fitScale) - 0.25, 0.5));
  };

  const handleZoomReset = () => {
    setUserZoom(null);
  };

  const zoomPercent = Math.round(currentScale * 100);

  return (
    <div style={styles.wrapper}>
      <div style={styles.zoomControls}>
        <button type="button" style={styles.zoomButton} onClick={handleZoomOut}>
          -
        </button>
        <button
          type="button"
          style={styles.zoomLabel}
          onClick={handleZoomReset}
        >
          {zoomPercent}%
        </button>
        <button type="button" style={styles.zoomButton} onClick={handleZoomIn}>
          +
        </button>
      </div>

      <div ref={containerRef} style={styles.scrollContainer}>
        {loading && (
          <div style={styles.loadingOverlay}>
            <div style={styles.spinner} />
          </div>
        )}

        <div style={{ position: 'relative', display: 'inline-block' }}>
          <canvas ref={canvasRef} style={{ display: 'block' }} />

          {currentPageFields.map((field, idx) => {
            const config = getFieldConfig(field.field_type);
            return (
              <div
                key={idx}
                style={{
                  position: 'absolute',
                  left: field.x * currentScale,
                  top: field.y * currentScale,
                  width: field.width * currentScale,
                  height: field.height * currentScale,
                  backgroundColor: config.bgColor,
                  border: `1px solid ${config.color}`,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10 * currentScale,
                  color: config.color,
                  pointerEvents: 'none',
                }}
              >
                {config.label}
              </div>
            );
          })}
        </div>
      </div>

      {numPages > 1 && (
        <div style={styles.pageNav}>
          <button
            type="button"
            style={{
              ...styles.pageButton,
              opacity: currentPage <= 1 ? 0.4 : 1,
            }}
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Prev
          </button>
          <span style={styles.pageLabel}>
            Page {currentPage} of {numPages}
          </span>
          <button
            type="button"
            style={{
              ...styles.pageButton,
              opacity: currentPage >= numPages ? 0.4 : 1,
            }}
            disabled={currentPage >= numPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  zoomControls: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderRadius: 8,
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    padding: '2px 4px',
  },
  zoomButton: {
    width: 32,
    height: 32,
    border: 'none',
    background: 'none',
    fontSize: 18,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  zoomLabel: {
    minWidth: 48,
    height: 32,
    border: 'none',
    background: 'none',
    fontSize: 12,
    color: '#6b7280',
    cursor: 'pointer',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollContainer: {
    flex: 1,
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    backgroundColor: '#e5e7eb',
    position: 'relative',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
    backgroundColor: 'rgba(229,231,235,0.8)',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #d1d5db',
    borderTopColor: '#2563eb',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  pageNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: '8px 16px',
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e5e5e5',
  },
  pageButton: {
    height: 44,
    padding: '0 20px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    fontSize: 15,
    fontWeight: 500,
    color: '#374151',
    cursor: 'pointer',
  },
  pageLabel: {
    fontSize: 14,
    color: '#6b7280',
  },
};

export default DocumentViewer;
